import React, { useState, useEffect } from 'react';
import { Table, Card, Btn, Modal, Input, Select, Textarea, FormGrid, Badge, NeedProject, StatCard, SectionHeader, RNLLoader } from '../components/UI';
import { fmt, fmtNum, fmtDate, exportCSV } from '../utils';
import * as api from '../api';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';

export default function LabourPage({ toast }) {
  const navigate = useNavigate();
  const { projectId, tab } = useParams();
  const [searchParams] = useSearchParams();
  const isAdmin = searchParams.get('admin') === 'true';
  const [entries, setEntries] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [compare, setCompare] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(null);
  const [form, setForm]       = useState({});
  const [saving, setSaving]   = useState(false);
  const [subsections, setSubsections] = useState([]);

  const load = async () => {
    if (!projectId) return;

    const cE = sessionStorage.getItem(`lab_e_${projectId}`);
    const cB = sessionStorage.getItem(`lab_b_${projectId}`);
    const cC = sessionStorage.getItem(`lab_c_${projectId}`);

    if (cE && cB && cC) {
      try {
        setEntries(JSON.parse(cE));
        setBudgets(JSON.parse(cB));
        setCompare(JSON.parse(cC));
        setLoading(false);
      } catch (e) {}
    } else {
      setLoading(true);
    }

    try {
      const [e, b, c, subs] = await Promise.all([
        api.listLabourEntries(projectId).catch(() => []),
        api.listLabourBudgets(projectId).catch(() => []),
        api.compareLabour(projectId).catch(() => null),
        api.listDmrSubsections(projectId).catch(() => []),
      ]);
      const arrE = Array.isArray(e) ? e : [];
      const arrB = Array.isArray(b) ? b : [];
      setEntries(arrE);
      setBudgets(arrB);
      setCompare(c);
      setSubsections(Array.isArray(subs) ? subs : []);
      sessionStorage.setItem(`lab_e_${projectId}`, JSON.stringify(arrE));
      sessionStorage.setItem(`lab_b_${projectId}`, JSON.stringify(arrB));
      sessionStorage.setItem(`lab_c_${projectId}`, JSON.stringify(c));
    } catch {}
    setLoading(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [projectId]);

  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (modal === 'entry')  await api.createLabourEntry({ ...form, project_id: projectId });
      if (modal === 'budget') await api.createLabourBudget({ ...form, project_id: projectId });
      toast.show('Saved'); setModal(null); load();
    } catch (e) { toast.show(e.message, 'error'); }
    setSaving(false);
  };

  if (!projectId) return <NeedProject />;

  const totalCost    = entries.reduce((s, e) => s + parseFloat(e.total_cost || 0), 0);
  const totalHours   = entries.reduce((s, e) => s + parseFloat(e.hours_worked || 0), 0);
  const totalBudget  = budgets.reduce((s, b) => s + parseFloat(b.budgeted_amount || 0), 0);

  return (
    <div className="fade-in">
      {!tab && (
        <>
          <SectionHeader title="Labour Cost Register" subtitle="Overview" />
          
          {loading ? (
            <RNLLoader title="Loading Labour Register..." subtitle="Please wait" />
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 24 }}>
              <StatCard label="Total Entries" value={entries.length} color="cyan" icon="⏱️" />
              <StatCard label="Total Hours" value={fmtNum(totalHours)} color="amber" icon="⚡" />
              <StatCard label="Total Cost vs Budget" value={`${fmt(totalCost)} / ${fmt(totalBudget)}`} color={totalCost > totalBudget ? "red" : "green"} icon="💰" />
            </div>
          )}

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <Card
              className="toggle-on-hover"
              onClick={() => navigate(`/projects/${projectId}/labour/entries`)}
              style={{ width: 280, padding: 24, cursor: 'pointer', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, border: '1px solid black' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'black'}
            >
              <span style={{ fontSize: 32, padding: 12, background: 'var(--accent-dim)', borderRadius: 16 }}>⏱️</span>
              <span style={{ fontWeight: 700, fontSize: 16 }}>Labour Entries</span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Manage daily timesheets and cost entries.</span>
            </Card>

            <Card
              className="toggle-on-hover"
              onClick={() => navigate(`/projects/${projectId}/labour/budgets`)}
              style={{ width: 280, padding: 24, cursor: 'pointer', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, border: '1px solid black' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'black'}
            >
              <span style={{ fontSize: 32, padding: 12, background: 'var(--accent-dim)', borderRadius: 16 }}>💹</span>
              <span style={{ fontWeight: 700, fontSize: 16 }}>Labour Budgets</span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Set budgeted hours and rates for trades.</span>
            </Card>

            <Card
              className="toggle-on-hover"
              onClick={() => navigate(`/projects/${projectId}/labour/comparison`)}
              style={{ width: 280, padding: 24, cursor: 'pointer', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, border: '1px solid black' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'black'}
            >
              <span style={{ fontSize: 32, padding: 12, background: 'var(--accent-dim)', borderRadius: 16 }}>⚖️</span>
              <span style={{ fontWeight: 700, fontSize: 16 }}>Comparison & Reporting</span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Compare actuals vs budget.</span>
            </Card>
          </div>
        </>
      )}

      {tab && (
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
          <div>
            <h2 style={{ fontSize:20, fontWeight:700 }}>Labour Cost Register</h2>
            <p style={{ fontSize:13, color:'var(--text-muted)', marginTop:2 }}>
              {entries.length} entries · {fmtNum(totalHours)} hrs · {fmt(totalCost)} spent / {fmt(totalBudget)} budgeted
            </p>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            {!isAdmin && tab==='entries' && <Btn onClick={()=>{setForm({entry_date:new Date().toISOString().slice(0,10)});setModal('entry');}}>+ Log Entry</Btn>}
            {!isAdmin && tab==='budgets' && <Btn onClick={()=>{setForm({});setModal('budget');}}>+ Set Budget</Btn>}
            <Btn variant="ghost" onClick={()=>exportCSV(tab==='entries'?entries:budgets, `labour-${tab}`)}>⬇ CSV</Btn>
          </div>
        </div>
      )}

      {tab==='entries' && (
        <Card>
          <Table loading={loading} cols={[
            {key:'entry_date',     label:'Date',       render:v=>fmtDate(v)},
            {key:'trade',          label:'Trade'},
            {key:'contractor_name',label:'Contractor'},
            {key:'headcount',      label:'Workers'},
            {key:'hours_worked',   label:'Hours',      render:v=>fmtNum(v)},
            {key:'hourly_rate',    label:'Rate',       render:v=>fmt(v)},
            {key:'total_cost',     label:'Total Cost', render:v=><span style={{fontFamily:'var(--mono)',fontWeight:700,color:'var(--accent)'}}>{fmt(v)}</span>},
            {key:'labour_entry_id',label:'',           render:id=>(
              <Btn variant="danger" size="sm" style={{padding:'3px 8px'}} onClick={async()=>{if(window.confirm('Delete?'))try{await api.deleteLabourEntry(id);toast.show('Deleted');load();}catch(e){toast.show(e.message,'error');}}}>Del</Btn>
            )},
          ]} rows={entries} />
        </Card>
      )}

      {tab==='budgets' && (
        <Card>
          <Table loading={loading} cols={[
            {key:'trade',            label:'Trade'},
            {key:'budgeted_hours',   label:'Budgeted Hours',  render:v=>fmtNum(v)},
            {key:'budgeted_rate',    label:'Rate',            render:v=>fmt(v)},
            {key:'budgeted_amount',  label:'Budget',          render:v=><span style={{fontFamily:'var(--mono)',fontWeight:700,color:'var(--accent)'}}>{fmt(v)}</span>},
            {key:'notes',            label:'Notes'},
          ]} rows={budgets} />
        </Card>
      )}

      {tab==='comparison' && (
        <Card>
          <Table loading={loading} cols={[
            {key:'trade',            label:'Trade'},
            {key:'budgeted_hours',   label:'Budget Hrs',  render:v=>fmtNum(v)},
            {key:'actual_hours',     label:'Actual Hrs',  render:v=>fmtNum(v)},
            {key:'budgeted_amount',  label:'Budget',      render:v=><span style={{fontFamily:'var(--mono)'}}>{fmt(v)}</span>},
            {key:'actual_amount',    label:'Actual',      render:v=><span style={{fontFamily:'var(--mono)'}}>{fmt(v)}</span>},
            {key:'amount_variance',  label:'Variance',    render:(v,row)=><span style={{fontFamily:'var(--mono)',fontWeight:700,color:row.is_over_budget?'var(--red)':'var(--green)'}}>{fmt(v)}</span>},
            {key:'is_over_budget',   label:'',            render:v=>v?<Badge color="red">Over Budget</Badge>:<Badge color="green">Within</Badge>},
          ]} rows={compare?.comparisons || []} />
        </Card>
      )}

      {modal==='entry' && (
        <Modal title="Log Labour Entry" onClose={()=>setModal(null)}>
          <div style={{display:'flex',flexDirection:'column',gap:14}}>
            <FormGrid>
              <Input label="Date"            type="date"   required value={form.entry_date||''} onChange={set('entry_date')} />
              <Select label="Sub-section" value={form.dmr_subsection_id || ''} onChange={set('dmr_subsection_id')}
                options={[{ value: '', label: '— Select Sub-section —' }, ...subsections.map(s => ({ value: s.id, label: s.name }))]} />
              <Input label="Trade"           required value={form.trade||''} onChange={set('trade')} placeholder="e.g. Carpenter" />
              <Input label="Contractor Name" value={form.contractor_name||''} onChange={set('contractor_name')} />
              <Input label="Head Count"      type="number" value={form.headcount||''} onChange={v=>setForm(f=>({...f,headcount:parseInt(v)||0}))} />
              <Input label="Hours Worked"    type="number" value={form.hours_worked||''} onChange={set('hours_worked')} />
              <Input label="Hourly Rate"     type="number" value={form.hourly_rate||''} onChange={set('hourly_rate')} />
              <Input label="Total Cost"      type="number" value={form.total_cost||''} onChange={set('total_cost')} />
            </FormGrid>
            <Textarea label="Remarks" value={form.remarks||''} onChange={set('remarks')} rows={2} />
            <div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
              <Btn variant="ghost" onClick={()=>setModal(null)}>Cancel</Btn>
              <Btn onClick={save} loading={saving}>Save Entry</Btn>
            </div>
          </div>
        </Modal>
      )}

      {modal==='budget' && (
        <Modal title="Set Labour Budget" onClose={()=>setModal(null)}>
          <div style={{display:'flex',flexDirection:'column',gap:14}}>
            <Input label="Trade" required value={form.trade||''} onChange={set('trade')} placeholder="e.g. Bricklayer" />
            <FormGrid>
              <Input label="Budgeted Hours"  type="number" value={form.budgeted_hours||''} onChange={set('budgeted_hours')} />
              <Input label="Budgeted Rate"   type="number" value={form.budgeted_rate||''} onChange={set('budgeted_rate')} />
              <Input label="Budgeted Amount" type="number" value={form.budgeted_amount||''} onChange={set('budgeted_amount')} />
            </FormGrid>
            <Textarea label="Notes" value={form.notes||''} onChange={set('notes')} rows={2} />
            <div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
              <Btn variant="ghost" onClick={()=>setModal(null)}>Cancel</Btn>
              <Btn onClick={save} loading={saving}>Save Budget</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
